import { createAsyncThunk } from '@reduxjs/toolkit';
import { getFirestore, doc, setDoc, updateDoc, getDoc, Timestamp } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { getApp } from 'firebase/app';
import { actions } from '../profileSlice';
import type { state } from 'app';
import { getUid } from './helper';

/**
 * Start a survey or optionally mark start of a stage
 */
export const startSurvey = createAsyncThunk(
  'survey/start',
  async ({ surveyId, currentStage }: { surveyId: string; currentStage?: 'survey' | 'followup' | 'followup12' }) => {
    const firestore = getFirestore();
    const uid = getUid();
    const surveyRef = doc(firestore, `users/${uid}/surveys`, surveyId);

    if (currentStage) {
      await updateDoc(doc(firestore, 'users', uid), { currentStage });
    }

    // Never overwrite an existing survey doc. A full set() here wipes recorded answers,
    // and this thunk is reachable for users whose survey is already underway or complete
    // (e.g. via a stray visit to createProfile).
    const existing = await getDoc(surveyRef);
    if (existing.exists()) {
      return {
        id: surveyId,
        currentStage,
        data: existing.data() as state.Survey,
      };
    }

    const data: state.Survey = { _totalRequired: 0, _totalOptional: 0,_completedOptional:0, _completedRequired: 0 ,_step: 0 };
    await setDoc(surveyRef, data);

    return {
      id: surveyId,
      currentStage,
      data,
    };
  },
);

/**
 * Update survey
 */
export const updateSurvey = createAsyncThunk(
  'profile/updateSurvey',
  async ({ id, data }: { id: string; data: Partial<state.Survey> }, thunkApi) => {
    const firestore = getFirestore();
    const uid = getUid();

    await updateDoc(doc(firestore, `users/${uid}/surveys`, id), data);
    return {
      id,
      data,
    };
  },
);

/**
 * Mark baseline or followup survey completed
 */

export const markCompleted = createAsyncThunk(
  'profile/markCompleted',
  async ({ scope, surveyId, isLast }: { scope: 'survey' | 'topic' | 'followup' | 'followup12' | 'coach-eval'; surveyId: string; isLast: boolean }, thunkApi) => {
    // Coach eval is a standalone survey — write postCoachingSurveyCompletedAt directly
    if (scope === 'coach-eval') {
      const firestore = getFirestore();
      const uid = getUid();
      const postCoachingSurveyCompletedAt = Timestamp.now();
      await updateDoc(doc(firestore, 'users', uid), { postCoachingSurveyCompletedAt });
      await updateDoc(doc(firestore, `users/${uid}/surveys`, surveyId), { completedAt: postCoachingSurveyCompletedAt });
      thunkApi.dispatch(actions.setPostCoachingSurveyCompleted({ postCoachingSurveyCompletedAt }));
      return;
    }

    if (isLast) {
      thunkApi.dispatch(actions.markLocalCompleted({ scope }));

      // Set followup unlock dates when baseline survey is fully completed
      if (scope === 'survey') {
        const firestore = getFirestore();
        const uid = getUid();
        const followupUnlockAt = Timestamp.fromMillis(
          Date.now() + Number(process.env.GATSBY_MILLS_UNLOCK_FOLLOWUP),
        );
        const followup12UnlockAt = Timestamp.fromMillis(
          Date.now() + Number(process.env.GATSBY_MILLS_UNLOCK_FOLLOWUP_12),
        );
        await updateDoc(doc(firestore, 'users', uid), {
          followupUnlockAt,
          followup12UnlockAt,
        });
        thunkApi.dispatch(actions.setFollowupUnlockDates({ followupUnlockAt, followup12UnlockAt }));
      }
    }

    const functions = getFunctions(getApp());
    await httpsCallable(
      functions,
      'completed',
    )({
      scope,
      surveyId,
      isLast,
    });

    // Re-fetch user doc to pick up fields set by the cloud function (e.g. coachAssisted)
    const firestore = getFirestore();
    const uid = getUid();
    const userSnap = await getDoc(doc(firestore, 'users', uid));
    if (userSnap.exists()) {
      thunkApi.dispatch(actions.syncUser(userSnap.data() as Partial<state.User>));
    }
  },
);
